import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import ProductCard from './ProductCard';
import { FaHeart as EmptyHeart, FaHeart as FilledHeart, FaArrowLeft, FaWhatsapp, FaFilter, FaTimes, FaPhone, FaStar, FaRegStar, FaStarHalfAlt, FaShoppingCart, FaComments, FaShare, FaChevronLeft, FaChevronRight, } from 'react-icons/fa';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faWhatsapp, faFacebookMessenger } from '@fortawesome/free-brands-svg-icons';
import { faShoppingCart, faComments } from '@fortawesome/free-solid-svg-icons';
import slugify from 'slugify';
import { v4 as uuidv4 } from 'uuid';
import ReactPixel from 'react-facebook-pixel';
import ReactGA from "react-ga4";
import ReactMarkdown from 'react-markdown';
import { Helmet } from 'react-helmet';
import styled, { keyframes, createGlobalStyle } from 'styled-components';
import AppConfig from '../config/appConfig'; // path আপনার ফোল্ডার স্ট্রাকচার অনুযায়ী ঠিক করুন

// ─── Global Styles ────────────────────────────────────────────────────────────
const GlobalStyle = createGlobalStyle`
  @import url('https://fonts.googleapis.com/css2?family=Tiro+Bangla:ital@0;1&family=Rajdhani:wght@400;500;600;700&display=swap');
`;

// ─── Animations ───────────────────────────────────────────────────────────────
const fadeInScale = keyframes`
  from { opacity: 0; transform: scale(0.9); }
  to   { opacity: 1; transform: scale(1); }
`;

const flyUpAndFade = keyframes`
  0%   { opacity: 1; transform: translateY(0); }
  100% { opacity: 0; transform: translateY(-100px); }
`;

const ripple = keyframes`
  0%   { transform: scale(0); opacity: 0.5; }
  100% { transform: scale(4); opacity: 0; }
`;

const btnPulse = keyframes`
  0%   { box-shadow: 0 0 0 0 rgba(230,126,34,0.5); }
  70%  { box-shadow: 0 0 0 10px rgba(230,126,34,0); }
  100% { box-shadow: 0 0 0 0 rgba(230,126,34,0); }
`;

const btnPulseGreen = keyframes`
  0%   { box-shadow: 0 0 0 0 rgba(37,211,102,0.5); }
  70%  { box-shadow: 0 0 0 10px rgba(37,211,102,0); }
  100% { box-shadow: 0 0 0 0 rgba(37,211,102,0); }
`;

const btnPulseBlue = keyframes`
  0%   { box-shadow: 0 0 0 0 rgba(0,132,255,0.5); }
  70%  { box-shadow: 0 0 0 10px rgba(0,132,255,0); }
  100% { box-shadow: 0 0 0 0 rgba(0,132,255,0); }
`;

const slideUp = keyframes`
  from { opacity: 0; transform: translateY(6px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const shimmer = keyframes`
  0%   { left: -80%; }
  100% { left: 130%; }
`;

// ─── Review Modal Styled Components ───────────────────────────────────────────
const ModalOverlay = styled.div`
  position: fixed;
  top: 0; left: 0; right: 0; bottom: 0;
  background-color: rgba(0,0,0,0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  font-family: 'Tiro Bangla', serif;
`;

const ModalContent = styled.div`
  background-color: #fff;
  padding: 30px;
  border-radius: 10px;
  box-shadow: 0 5px 15px rgba(0,0,0,0.3);
  width: 90%;
  max-width: 500px;
  position: relative;
  animation: ${fadeInScale} 0.3s ease-out forwards;

  h2 {
    text-align: center;
    margin-bottom: 25px;
    color: #333;
    font-size: 1.8em;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 15px; right: 15px;
  background: none; border: none;
  font-size: 1.5em; color: #888;
  cursor: pointer;
  transition: color 0.2s ease;
  &:hover { color: #333; }
`;

const FormGroup = styled.div`
  margin-bottom: 20px;

  label {
    display: block;
    margin-bottom: 8px;
    font-weight: bold;
    color: #555;
  }

  input[type="text"], textarea {
    width: 100%;
    padding: 12px;
    border: 1px solid #ddd;
    border-radius: 6px;
    font-size: 1em;
    font-family: inherit;
    box-sizing: border-box;
    &:focus {
      outline: none;
      border-color: #007bff;
      box-shadow: 0 0 0 3px rgba(0,123,255,0.25);
    }
  }

  textarea { resize: vertical; min-height: 100px; }
`;

const StarRatingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 20px;
  font-size: 1.8em;
  color: #FFD700;

  span {
    cursor: pointer;
    transition: transform 0.2s ease;
    &:hover { transform: scale(1.1); }
  }
  span:last-child {
    margin-left: 10px;
    font-size: 0.7em;
    color: #666;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 15px;
  margin-top: 25px;

  button {
    padding: 12px 25px;
    border: none;
    border-radius: 6px;
    font-size: 1em;
    cursor: pointer;
    transition: background-color 0.3s ease, transform 0.2s ease;

    &:first-child {
      background-color: #28a745;
      color: #fff;
      &:hover { background-color: #218838; transform: translateY(-2px); }
    }
    &:last-child {
      background-color: #6c757d;
      color: #fff;
      &:hover { background-color: #5a6268; transform: translateY(-2px); }
    }
  }
`;

// ─── Review Modal Component ────────────────────────────────────────────────────
const ReviewModal = ({ onClose, onSubmit, productName, userName, isLoggedIn }) => {
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');
  const [customerName, setCustomerName] = useState('');

  useEffect(() => {
    if (isLoggedIn && userName && userName !== 'Anonymous User' && userName !== 'User') {
      setCustomerName(userName);
    } else {
      setCustomerName('');
    }
  }, [userName, isLoggedIn]);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(rating, comment, customerName);
  };

  const renderStarInput = (index) => (
    <span key={index} onClick={() => setRating(index)}>
      {rating >= index ? <FaStar /> : <FaRegStar />}
    </span>
  );

  return (
    <ModalOverlay>
      <ModalContent>
        <CloseButton onClick={onClose}><FaTimes /></CloseButton>
        <h2>{productName} এর জন্য একটি রিভিউ যোগ করুন</h2>
        <form onSubmit={handleSubmit}>
          <StarRatingContainer>
            {[1, 2, 3, 4, 5].map((index) => renderStarInput(index))}
            <span>({rating} Star{rating !== 1 ? 's' : ''})</span>
          </StarRatingContainer>
          <FormGroup>
            <label htmlFor="customerName">আপনার নাম:</label>
            <input
              type="text"
              id="customerName"
              value={customerName}
              onChange={(e) => setCustomerName(e.target.value)}
              placeholder="আপনার নাম লিখুন"
              required
            />
          </FormGroup>
          <FormGroup>
            <label htmlFor="comment">আপনার মন্তব্য:</label>
            <textarea
              id="comment"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              rows="5"
              placeholder="আপনার মতামত লিখুন..."
              required
            />
          </FormGroup>
          <ButtonContainer>
            <button type="submit">জমা দিন</button>
            <button type="button" onClick={onClose}>বাতিল করুন</button>
          </ButtonContainer>
        </form>
      </ModalContent>
    </ModalOverlay>
  );
};

// ─── ImageZoom (file 1 logic, unchanged) ──────────────────────────────────────
const ZOOM_FACTOR = 2.5;
const LENS_SIZE = 120;

const ImageZoom = ({ src, alt }) => {
  const containerRef = useRef(null);
  const [isHovering, setIsHovering] = useState(false);
  const [lensStyle, setLensStyle] = useState({});
  const [zoomBgStyle, setZoomBgStyle] = useState({});
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const handleMouseMove = (e) => {
    if (isMobile) return;
    const container = containerRef.current;
    if (!container) return;
    const rect = container.getBoundingClientRect();
    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;
    const halfLens = LENS_SIZE / 2;
    let lensX = Math.max(0, Math.min(mouseX - halfLens, rect.width - LENS_SIZE));
    let lensY = Math.max(0, Math.min(mouseY - halfLens, rect.height - LENS_SIZE));
    const bgX = -((lensX / rect.width) * rect.width * ZOOM_FACTOR);
    const bgY = -((lensY / rect.height) * rect.height * ZOOM_FACTOR);
    setLensStyle({ left: lensX, top: lensY, width: LENS_SIZE, height: LENS_SIZE });
    setZoomBgStyle({
      backgroundImage: `url(${src})`,
      backgroundSize: `${rect.width * ZOOM_FACTOR}px ${rect.height * ZOOM_FACTOR}px`,
      backgroundPosition: `${bgX}px ${bgY}px`,
      backgroundRepeat: 'no-repeat',
    });
  };

  if (isMobile) {
    return (
      <div style={{ width: '100%', aspectRatio: '1 / 1', overflow: 'hidden', display: 'block', marginBottom: '6px' }}>
        <img
          src={src}
          alt={alt}
          style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}
          draggable={false}
        />
      </div>
    );
  }

  return (
    <div style={{ position: 'relative', userSelect: 'none' }}>
      <MainImageWrapper
        ref={containerRef}
        onMouseEnter={() => setIsHovering(true)}
        onMouseLeave={() => setIsHovering(false)}
        onMouseMove={handleMouseMove}
      >
        <MainImage src={src} alt={alt} draggable={false} />
        {isHovering && (
          <LensOverlay style={{ ...lensStyle }} />
        )}
      </MainImageWrapper>
      {isHovering && (
        <ZoomPopup style={{ ...zoomBgStyle }}>
          <ZoomLabel>{ZOOM_FACTOR}× zoom</ZoomLabel>
        </ZoomPopup>
      )}
    </div>
  );
};

// ─── Main Layout Styled Components ────────────────────────────────────────────
const PageWrapper = styled.div`
  background-color: #fff;
  font-family: 'Tiro Bangla', serif;
`;

const Breadcrumb = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 8px 16px;
  border-bottom: 1px solid #e9ecef;

  @media (min-width: 768px) { padding: 12px 24px; }
`;

const BackLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 0.85em;
  color: #148f77;
  text-decoration: none;
  transition: color 0.3s ease;
  &:hover { color: #0056b3; }
`;

const ProductSection = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0;

  @media (min-width: 768px) { padding: 32px 24px; }
`;

const ProductGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 0;

  @media (min-width: 1024px) {
    grid-template-columns: 5fr 4fr 3fr;
    gap: 32px;
  }
`;

// ─── Image Column ──────────────────────────────────────────────────────────────
const ImageGallery = styled.div`
  position: relative;
  background: #fff;
  border: none;
  border-radius: 0;
  padding: 0;

  @media (min-width: 768px) {
    border: 1px solid #e9ecef;
    border-radius: 8px;
    padding: 16px;
    overflow: visible;
  }
`;

const StickyImageInner = styled.div`
  @media (min-width: 768px) {
    position: sticky;
    top: 16px;
    overflow: visible;
  }
`;

const BadgeDiscount = styled.div`
  position: absolute;
  top: 6px; left: 6px;
  background-color: #dc3545;
  color: #fff;
  padding: 4px 10px;
  border-radius: 5px;
  font-size: 0.8em;
  font-weight: bold;
  z-index: 10;

  @media (min-width: 768px) {
    top: 16px; left: 16px;
    font-size: 0.9em;
    padding: 8px 15px;
  }
`;

const BadgePopular = styled(BadgeDiscount)`
  background-color: #007bff;
`;

const FavoriteIcon = styled.button`
  position: absolute;
  top: 6px; right: 6px;
  background: none; border: none;
  font-size: 1.3em;
  cursor: pointer;
  color: ${({ isFav }) => (isFav ? '#ff0000' : '#ccc')};
  transition: color 0.2s ease;
  z-index: 20;
  &:hover { color: #ff0000; }

  @media (min-width: 768px) {
    top: 16px; right: 16px;
    font-size: 1.5em;
  }
`;

const MainImageWrapper = styled.div`
  width: 100%;
  height: 250px;
  aspect-ratio: 1 / 1; 
  overflow: hidden;
  border-radius: 0;
  margin-bottom: 6px;
  position: relative;
  cursor: crosshair;

  @media (min-width: 768px) {
    height: auto;
    aspect-ratio: 1 / 1; 
    border-radius: 8px;
    border: 1px solid #e9ecef;
    margin-bottom: 16px;
  }
`;

const MainImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
  background: #fff; 
`;

const LensOverlay = styled.div`
  position: absolute;
  pointer-events: none;
  border: 2px solid #e67e22;
  background: rgba(230, 126, 34, 0.15);
  box-shadow: 0 0 0 1px rgba(230,126,34,0.3);
  z-index: 10;
`;

const ZoomPopup = styled.div`
  position: absolute;
  top: 0;
  left: calc(100% + 16px);
  width: 420px;
  height: 400px;
  border-radius: 8px;
  border: 1px solid #e9ecef;
  box-shadow: 0 8px 32px rgba(0,0,0,0.18);
  overflow: hidden;
  background: #fff;
  z-index: 50;
`;

const ZoomLabel = styled.div`
  position: absolute;
  top: 8px; right: 8px;
  background: rgba(0,0,0,0.4);
  color: #fff;
  font-size: 0.75em;
  padding: 2px 8px;
  border-radius: 4px;
`;

const ThumbnailsContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: 8px;
  overflow-x: auto;
  padding: 4px 8px 8px;

  @media (min-width: 768px) {
    justify-content: flex-start;
    padding: 0;
  }
`;

const Thumbnail = styled.button`
  flex-shrink: 0;
  width: 48px; height: 48px;
  border: 2px solid ${({ active }) => (active ? '#e67e22' : '#ddd')};
  border-radius: 5px;
  overflow: hidden;
  cursor: pointer;
  padding: 0;
  background: none;
  transition: border-color 0.2s ease;
  &:hover { border-color: #e67e22; }

  img { width: 100%; height: 100%; object-fit: cover; }

  @media (min-width: 768px) {
    width: 64px; height: 64px;
  }
`;

// ─── Info Column ───────────────────────────────────────────────────────────────
const InfoWrapper = styled.div`
  border: 1px solid #e9ecef;
  border-radius: 0;
  padding: 12px;
  background: #fff;
  margin-top: 4px;

  @media (min-width: 768px) {
    border-radius: 8px;
    padding: 24px;
    margin-top: 0;
  }
`;

const ProductName = styled.h1`
  font-size: 1em;
  font-weight: bold;
  color: #333;
  margin: 0 0 6px;

  @media (min-width: 768px) {
    font-size: 1.8em;
    font-weight: normal;
    margin-bottom: 10px;
  }
`;

// ─── Timer ─────────────────────────────────────────────────────────────────────
const TimerContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: 8px;
  background: #fff5f5;
  border: 1px solid #f5c6cb;
  border-radius: 8px;
  padding: 8px 12px;
  margin-bottom: 12px;

  @media (min-width: 768px) {
    gap: 15px;
    padding: 10px 15px;
    margin-bottom: 20px;
  }
`;

const TimeBox = styled.div`
  background-color: #fff;
  border: 1px solid #f5c6cb;
  border-radius: 5px;
  padding: 5px 8px;
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 45px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.05);

  @media (min-width: 768px) {
    padding: 8px 12px;
    min-width: 60px;
  }
`;

const TimeValue = styled.span`
  font-size: 1.4em;
  font-weight: bold;
  color: #e67e22;
  line-height: 1;

  @media (min-width: 768px) { font-size: 1.8em; }
`;

const TimeLabel = styled.span`
  font-size: 0.7em;
  color: #721c24;
  font-family: 'Tiro Bangla', serif;
  margin-top: 2px;

  @media (min-width: 768px) {
    font-size: 0.8em;
    margin-top: 5px;
  }
`;

// ─── Rating ────────────────────────────────────────────────────────────────────
const RatingDisplay = styled.div`
  display: flex;
  align-items: center;
  color: #FFD700;
  font-size: 0.9em;

  span {
    margin-left: 5px;
    color: #666;
    font-size: 0.9em;
  }

  @media (min-width: 768px) { font-size: 1.1em; }
`;

const StockInfo = styled.p`
  font-size: 0.9em;
  font-weight: bold;
  color: ${({ hasStock }) => (hasStock ? '#28a745' : '#dc3545')};
  margin: 4px 0 8px;

  @media (min-width: 768px) { font-size: 1.1em; margin-bottom: 15px; }
`;

const BuyersInfo = styled.p`
  font-size: 0.8em;
  color: #777;
  font-weight: bold;
  margin-bottom: 5px;

  @media (min-width: 768px) { font-size: 1em; margin-bottom: 10px; }
`;

const PriceContainer = styled.div`
  display: flex;
  align-items: baseline;
  gap: 8px;
  margin-bottom: 12px;

  @media (min-width: 768px) { margin-bottom: 20px; }
`;

const CurrentPrice = styled.span`
  font-size: 1.7em;
  font-weight: bold;
  color: #e67e22;

  @media (min-width: 768px) { font-size: 2.2em; }
`;

const OriginalPrice = styled.span`
  font-size: 1em;
  color: #888;
  text-decoration: line-through;

  @media (min-width: 768px) { font-size: 1.3em; }
`;

const DiscountBadge = styled.span`
  font-size: 0.8em;
  color: #dc3545;
  font-weight: bold;
`;

// ─── Color/Size Selection ──────────────────────────────────────────────────────
const OptionGroup = styled.div`
  margin-bottom: 14px;
  padding-bottom: 14px;
  border-bottom: 1px solid #e9ecef;

  h4 {
    font-size: 0.8em;
    font-weight: bold;
    color: #333;
    margin: 0 0 8px;
  }

  @media (min-width: 768px) {
    margin-bottom: 20px;
    padding-bottom: 20px;
    h4 { font-size: 1.1em; }
  }
`;

const ColorOptionWrapper = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin: 0 6px 6px 0;
  padding: 4px 10px;
  border: 2px solid ${({ active }) => (active ? '#e67e22' : '#ddd')};
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.8em;
  transition: border-color 0.2s ease;
  &:hover { border-color: #e67e22; }

  @media (min-width: 768px) { font-size: 0.95em; padding: 6px 14px; }
`;

const ColorBox = styled.div`
  width: 20px; height: 20px;
  border-radius: 50%;
  background-color: ${({ colorHex }) => colorHex};
  border: 1px solid #ddd;
  flex-shrink: 0;

  @media (min-width: 768px) { width: 26px; height: 26px; }
`;

const SizeBox = styled.button`
  padding: 5px 12px;
  border: 2px solid ${({ active }) => (active ? '#e67e22' : '#ddd')};
  border-radius: 5px;
  cursor: pointer;
  font-size: 0.8em;
  color: #555;
  background: none;
  margin: 0 6px 6px 0;
  transition: border-color 0.2s ease;
  font-weight: ${({ active }) => (active ? 'bold' : 'normal')};
  &:hover { border-color: #e67e22; }

  @media (min-width: 768px) { padding: 8px 15px; font-size: 0.95em; }
`;

// ─── Size Chart ────────────────────────────────────────────────────────────────
const SizeChartToggle = styled.button`
  font-size: 0.75em;
  color: #007bff;
  background: none;
  border: none;
  cursor: pointer;
  text-decoration: underline;
  padding: 0;
  margin-bottom: 8px;
  &:hover { color: #0056b3; }
`;

const SizeChartTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.75em;

  th, td {
    border: 1px solid #e9ecef;
    padding: 6px 8px;
    text-align: center;
  }
  th { font-weight: 600; }
  tr:nth-child(even) { background: #f8f9fa; }
`;

// ─── Description Preview ───────────────────────────────────────────────────────
const DescriptionPreview = styled.div`
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  overflow: hidden;
  margin-bottom: 12px;

  h4 {
    font-size: 0.8em;
    font-weight: bold;
    color: #333;
    padding: 8px 12px 6px;
    margin: 0;
  }

  @media (min-width: 768px) {
    margin-bottom: 20px;
    h4 { font-size: 0.95em; padding: 14px 16px 8px; }
  }
`;

const DescriptionBody = styled.div`
  max-height: 80px;
  overflow: hidden;
  padding: 0 12px;
  font-size: 0.8em;
  color: #555;
  line-height: 1.5;
  position: relative;

  @media (min-width: 768px) { padding: 0 16px; font-size: 0.95em; }
`;

const SeeMoreGradient = styled.div`
  height: 60px;
  background: linear-gradient(to bottom, transparent, rgba(248,249,250,0.85) 50%, rgb(248,249,250));
  display: flex;
  align-items: flex-end;
  justify-content: center;
  padding-bottom: 8px;
`;

const SeeMoreBtn = styled.button`
  font-size: 0.75em;
  font-weight: bold;
  color: #e67e22;
  border: 1px solid #e67e22;
  background: #fff;
  padding: 3px 10px;
  border-radius: 20px;
  cursor: pointer;
  &:hover { color: #c0392b; border-color: #c0392b; }
`;

// ─── Share Button ──────────────────────────────────────────────────────────────
const ShareButton = styled.button`
  width: 100%;
  padding: 8px 12px;
  background: #fff;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 0.8em;
  color: #555;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  transition: all 0.25s ease;
  &:hover {
    background: #f8f9fa;
    border-color: #bbb;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0,0,0,0.08);
  }
  &:active { transform: translateY(0) scale(0.97); }

  @media (min-width: 768px) { font-size: 0.95em; padding: 10px 16px; }
`;

// ─── Buy Box Column ────────────────────────────────────────────────────────────
const BuyBox = styled.div`
  border: 1px solid #ddd;
  border-radius: 0;
  padding: 10px;
  background: #fff;
  margin-top: 4px;

  @media (min-width: 768px) {
    border-radius: 8px;
    padding: 16px;
    position: sticky;
    top: 16px;
    margin-top: 0;
  }
`;

const BuyBoxPrice = styled.div`
  display: none;
  @media (min-width: 768px) {
    display: block;
    font-size: 2em;
    font-weight: normal;
    color: #c0392b;
    margin-bottom: 8px;
  }
`;

const BuyBoxOriginalPrice = styled.div`
  display: none;
  @media (min-width: 768px) {
    display: block;
    font-size: 0.9em;
    color: #888;
    margin-bottom: 16px;
  }
`;

const BuyBoxStock = styled.div`
  display: none;
  @media (min-width: 768px) {
    display: block;
    font-size: 0.9em;
    margin-bottom: 16px;
    padding-bottom: 16px;
    border-bottom: 1px solid #e9ecef;
    font-weight: bold;
    color: ${({ hasStock }) => (hasStock ? '#28a745' : '#dc3545')};
  }
`;

const BuyBoxButtons = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;

  @media (min-width: 768px) { gap: 12px; }
`;

const ActionButtonPrimary = styled.button`
  width: 100%;
  padding: 10px 16px;
  display: flex;           // ✅ যোগ করুন
  align-items: center;     // ✅ যোগ করুন
  justify-content: center; // ✅ যোগ করুন
  gap: 8px;                // ✅ যোগ করুন
  border: none;
  border-radius: 8px;
  font-size: 0.85em;
  font-weight: bold;
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  transition: all 0.25s ease;
  background-color: ${({ disabled }) => (disabled ? '#ccc' : '#e67e22')};
  color: ${({ disabled }) => (disabled ? '#888' : '#fff')};
  position: relative;
  overflow: hidden;
  animation: ${slideUp} 0.3s ease both;

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: -80%;
    width: 60%;
    height: 100%;
    background: linear-gradient(
      120deg,
      transparent 0%,
      rgba(255,255,255,0.35) 50%,
      transparent 100%
    );
    animation: ${shimmer} 2.5s ease-in-out infinite;
  }

  &:hover:not(:disabled) {
    background-color: #d35400;
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(230,126,34,0.4);
    animation: ${btnPulse} 1.2s ease-out infinite;
  }
  &:active:not(:disabled) {
    transform: translateY(0px) scale(0.97);
    box-shadow: none;
  }

  @media (min-width: 768px) { font-size: 1em; padding: 12px 16px; }
`;

const ActionButtonSecondary = styled(ActionButtonPrimary)`
  background-color: ${({ disabled }) => (disabled ? '#ccc' : '#f0c040')};
  color: ${({ disabled }) => (disabled ? '#888' : '#333')};

  &::after {
    background: linear-gradient(
      120deg,
      transparent 0%,
      rgba(255,255,255,0.45) 50%,
      transparent 100%
    );
    animation: ${shimmer} 2.8s ease-in-out infinite;
  }

  &:hover:not(:disabled) {
    background-color: #e0b030;
    box-shadow: 0 6px 20px rgba(240,192,64,0.45);
    animation: none;
  }
`;

const TwoColumnButtons = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 6px;

  @media (min-width: 768px) {
    grid-template-columns: 1fr;
    gap: 12px;
  }
`;

const ActionButtonCall = styled.button`
  padding: 8px 10px;
  border: 2px solid #ddd;
  border-radius: 8px;
  font-size: 0.75em;
  font-weight: bold;
  background: #fff;
  color: #333;
  cursor: pointer;
  position: relative;
  overflow: hidden;
  transition: all 0.25s ease;
  &:hover {
    background: #f8f9fa;
    border-color: #bbb;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0,0,0,0.1);
  }
  &:active { transform: translateY(0) scale(0.97); box-shadow: none; }

  @media (min-width: 768px) {
    width: 100%;
    padding: 10px 16px;
    font-size: 0.9em;
  }
`;

const ActionButtonWhatsApp = styled(ActionButtonCall)`
  background: #25d366;
  color: #fff;
  border-color: #25d366;
  position: relative;
  overflow: hidden;

  &::after {
    content: '';
    position: absolute;
    top: 0; left: -80%;
    width: 60%; height: 100%;
    background: linear-gradient(120deg, transparent 0%, rgba(255,255,255,0.3) 50%, transparent 100%);
    animation: ${shimmer} 2.6s ease-in-out infinite;
  }

  &:hover {
    background: #1ebe5d;
    border-color: #1ebe5d;
    box-shadow: 0 4px 16px rgba(37,211,102,0.45);
    animation: ${btnPulseGreen} 1.2s ease-out infinite;
  }
`;

const ActionButtonMessenger = styled(ActionButtonCall)`
  background: #0084ff;
  color: #fff;
  border-color: #0084ff;
  grid-column: ${({ spanFull }) => (spanFull ? 'span 2' : 'auto')};
  position: relative;
  overflow: hidden;

  &::after {
    content: '';
    position: absolute;
    top: 0; left: -80%;
    width: 60%; height: 100%;
    background: linear-gradient(120deg, transparent 0%, rgba(255,255,255,0.3) 50%, transparent 100%);
    animation: ${shimmer} 3s ease-in-out infinite;
  }

  &:hover {
    background: #006ecc;
    border-color: #006ecc;
    box-shadow: 0 4px 16px rgba(0,132,255,0.45);
    animation: ${btnPulseBlue} 1.2s ease-out infinite;
  }

  @media (min-width: 768px) { grid-column: auto; width: 100%; }
`;

const TrustBadges = styled.div`
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px solid #e9ecef;
  font-size: 0.7em;
  color: #666;

  div { margin-bottom: 3px; }

  @media (min-width: 768px) {
    margin-top: 16px;
    padding-top: 16px;
    font-size: 0.85em;
    div { margin-bottom: 5px; }
  }
`;

// ─── Tabs Section ──────────────────────────────────────────────────────────────
const TabsSection = styled.div`
  max-width: 1200px;
  margin: 16px auto 0;
  border: 1px solid #e9ecef;
  border-radius: 0;
  background: #fff;
  padding: 12px;

  @media (min-width: 768px) {
    margin-top: 48px;
    border-radius: 8px;
    padding: 24px;
  }
`;

const TabButtons = styled.div`
  display: flex;
  border-bottom: 2px solid #e9ecef;
  margin-bottom: 16px;

  @media (min-width: 768px) { margin-bottom: 24px; }
`;

const TabButton = styled.button`
  padding: 8px 12px;
  background: ${({ active }) => (active ? '#148f77' : 'transparent')};
  color: ${({ active }) => (active ? '#fff' : '#495057')};
  border: none;
  border-top-left-radius: 6px;
  border-top-right-radius: 6px;
  cursor: pointer;
  font-size: 0.8em;
  font-weight: bold;
  font-family: 'Tiro Bangla', serif;
  transition: background 0.3s, color 0.3s;
  &:hover { background: ${({ active }) => (active ? '#0e6251' : '#e9ecef')}; color: ${({ active }) => (active ? '#fff' : '#333')}; }

  @media (min-width: 768px) {
    padding: 12px 20px;
    font-size: 1.05em;
  }
`;

// ─── Spec Table ────────────────────────────────────────────────────────────────
const SpecContainer = styled.div`
  .spec-item {
    display: flex;
    border-bottom: 1px solid #e9ecef;
    &:last-child { border-bottom: none; }
    &:nth-child(even) { background: #f8f9fa; }
  }
  .spec-key {
    font-size: 0.8em;
    font-weight: bold;
    color: #555;
    padding: 8px 10px;
    width: 35%;
    display: flex;
    align-items: center;
    gap: 6px;

    @media (min-width: 768px) { font-size: 0.95em; padding: 12px 16px; }
  }
  .spec-bullet {
    width: 8px; height: 8px;
    border-radius: 50%;
    background: #e67e22;
    flex-shrink: 0;
  }
  .spec-value {
    font-size: 0.8em;
    color: #333;
    padding: 8px 10px;
    flex: 1;

    @media (min-width: 768px) { font-size: 0.95em; padding: 12px 16px; }
  }
`;

// ─── Description Section ───────────────────────────────────────────────────────
const DescriptionSection = styled.div`
  margin-top: 24px;
  font-family: 'Tiro Bangla', serif;

  h3 {
    font-size: 1em;
    font-weight: bold;
    color: #333;
    margin-bottom: 12px;

    @media (min-width: 768px) { font-size: 1.4em; margin-bottom: 16px; }
  }
`;

const DescriptionItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 6px 0;
  border-left: ${({ isSpecial }) => (isSpecial ? '4px solid #3498db' : 'none')};
  padding-left: ${({ isSpecial }) => (isSpecial ? '12px' : '4px')};
  font-weight: ${({ isSpecial }) => (isSpecial ? '600' : '400')};
  color: ${({ isSpecial }) => (isSpecial ? '#2c3e50' : '#34495e')};
  box-shadow: ${({ isSpecial }) => (isSpecial ? '0 2px 4px rgba(0,0,0,0.06)' : 'none')};
  border-radius: ${({ isSpecial }) => (isSpecial ? '4px' : '0')};

  .icon { font-size: 1.2em; color: ${({ isSpecial }) => (isSpecial ? '#e67e22' : '#27ae60')}; }
  .text { font-size: 0.85em; line-height: 1.6; @media (min-width: 768px) { font-size: 1em; } }
`;

// ─── Video Section ─────────────────────────────────────────────────────────────
const VideoSection = styled.div`
  margin-top: 24px;
  h3 { font-size: 1em; font-weight: bold; color: #333; margin-bottom: 10px; @media (min-width: 768px) { font-size: 1.3em; } }
`;

const YouTubeEmbed = styled.div`
  position: relative;
  width: 100%;
  padding-bottom: 56.25%;
  height: 0;
  iframe {
    position: absolute;
    top: 0; left: 0;
    width: 100%; height: 100%;
    border-radius: 8px;
    border: none;
  }
`;

// ─── Reviews ───────────────────────────────────────────────────────────────────
const RatingSummary = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  padding: 12px;
  margin-bottom: 16px;

  .avg { font-size: 2.5em; font-weight: bold; color: #333; }
  .stars { color: #FFD700; font-size: 1em; display: flex; }
  .count { font-size: 0.8em; color: #666; }

  @media (min-width: 768px) { padding: 16px; margin-bottom: 24px; }
`;

const ReviewCard = styled.div`
  background: #f9f9f9;
  border: 1px solid #eee;
  border-radius: 8px;
  padding: 10px;
  margin-bottom: 10px;
  font-family: 'Tiro Bangla', serif;

  @media (min-width: 768px) { padding: 15px; margin-bottom: 15px; }
`;

const ReviewHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;

  .name { font-weight: bold; color: #333; font-size: 0.85em; @media (min-width: 768px) { font-size: 1em; } }
  .stars { color: #FFD700; font-size: 0.9em; display: flex; @media (min-width: 768px) { font-size: 1.1em; } }
`;

const ReviewComment = styled.p`
  font-size: 0.8em;
  color: #666;
  line-height: 1.6;
  margin: 0;

  @media (min-width: 768px) { font-size: 1em; }
`;

const AddReviewSection = styled.div`
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid #eee;
  text-align: center;

  button {
    background-color: #28a745;
    color: #fff;
    border: none;
    padding: 8px 18px;
    border-radius: 5px;
    cursor: pointer;
    font-size: 0.85em;
    font-family: 'Tiro Bangla', serif;
    transition: background-color 0.3s ease;
    &:hover { background-color: #218838; }
    @media (min-width: 768px) { padding: 10px 20px; font-size: 1em; }
  }
`;

// ─── Related Products ──────────────────────────────────────────────────────────
const RelatedSection = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 8px 0 16px;

  @media (min-width: 768px) { padding: 0 24px 32px; }
`;

const RelatedInner = styled.div`
  border: 1px solid #e9ecef;
  border-radius: 0;
  padding: 12px;
  background: #fff;

  h2 {
    font-size: 1em;
    font-weight: bold;
    color: #333;
    margin: 0 0 12px;
    font-family: 'Rajdhani', sans-serif;
  }

  @media (min-width: 768px) {
    border-radius: 8px;
    padding: 24px;
    h2 { font-size: 1.5em; margin-bottom: 24px; }
  }
`;

const RelatedGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;

  @media (min-width: 768px) { display: none; }
`;

const RelatedScroll = styled.div`
  display: none;

  @media (min-width: 768px) {
    display: block;
    position: relative;
  }
`;

const RelatedScrollInner = styled.div`
  overflow-x: auto;
  scrollbar-width: none;
  &::-webkit-scrollbar { display: none; }
  scroll-behavior: smooth;
`;

const RelatedFlex = styled.div`
  display: flex;
  gap: 16px;
  padding-bottom: 8px;

  > div { flex-shrink: 0; width: 280px; }
`;

const ScrollArrow = styled.button`
  position: absolute;
  top: 50%;
  ${({ dir }) => (dir === 'left' ? 'left: -20px;' : 'right: -20px;')}
  transform: translateY(-50%);
  z-index: 10;
  width: 40px; height: 40px;
  background: #fff;
  border: 1px solid #ddd;
  border-radius: 50%;
  box-shadow: 0 2px 8px rgba(0,0,0,0.12);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #555;
  transition: box-shadow 0.2s;
  &:hover { box-shadow: 0 4px 16px rgba(0,0,0,0.18); background: #f8f9fa; }
`;



// ─── Flying Cart Animation ──────────────────────────────────────────────────────
const FlyingCartItem = styled.div`
  position: fixed;
  pointer-events: none;
  z-index: 9999;
  color: #e67e22;
  font-size: 1.4em;
  font-weight: bold;
  display: flex;
  align-items: center;
  gap: 4px;
  transform: translate(-50%, -50%);
  animation: ${flyUpAndFade} 1.2s ease-out forwards;
`;

// ─── Main Component ────────────────────────────────────────────────────────────
function ProductDetails({ addToCart, cartItem, toggleFavorite, isFavorite, getAuthHeaders }) {
  const navigate = useNavigate();
  const { id } = useParams();
  const [eventId] = useState(uuidv4());

  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [activeImage, setActiveImage] = useState('');
  const [flyingCart, setFlyingCart] = useState(null);
  const addCartButtonRef = useRef(null);

  const [activeTab, setActiveTab] = useState('specification');
  const [selectedColor, setSelectedColor] = useState('');
  const [selectedSize, setSelectedSize] = useState('');

  const [remainingTime, setRemainingTime] = useState(0);
  const [userSessionId, setUserSessionId] = useState(null);
  const [timerActive, setTimerActive] = useState(false);

  const [showReviewModal, setShowReviewModal] = useState(false);
  const [currentUserName, setCurrentUserName] = useState('');
  const [showSizeChart, setShowSizeChart] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [relatedScrollPosition, setRelatedScrollPosition] = useState(0);
  const relatedContainerRef = useRef(null);

  const [pdpSettings, setPdpSettings] = useState({
    callButton: { enabled: true, text: '📞 Call', phoneNumber: `+88${AppConfig.phoneNumber}` },
    whatsappButton: { enabled: true, text: 'WhatsApp Us', phoneNumber: `+88${AppConfig.WhatsAppNumber}` },
    messengerButton: { enabled: true, text: 'Chat on Messenger', messengerLink: AppConfig.MessengerLink || 'https://m.me/elevateemart' },
    secureTransaction: { enabled: true, text: '🔒 Secure transaction' },
    cashOnDelivery: { enabled: true, text: '✓ Cash on Delivery available' },
  });

  const TIMER_RESET_DAYS = 5;
  const memoizedGetAuthHeaders = useCallback(getAuthHeaders, [getAuthHeaders]);
  const API_BASE_URL = `${AppConfig.API_BASE_URL}`;

  const checkLoginStatus = useCallback(() => {
    const token = localStorage.getItem('authToken');
    setIsLoggedIn(!!token);
  }, []);

  const fetchUserProfile = useCallback(async () => {
    const token = localStorage.getItem('authToken');
    if (!token) { setCurrentUserName(''); return; }
    try {
      const response = await fetch(`${API_BASE_URL}/api/user-profile`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const userData = await response.json();
        setCurrentUserName(userData.name || 'Anonymous User');
      } else {
        setCurrentUserName('Anonymous User');
      }
    } catch (error) {
      setCurrentUserName('Anonymous User');
    }
  }, []);

  const handleShare = async () => {
    const titleSlug = slugify(product.name, { lower: true, strict: true, trim: true });
    const shareUrl = `https://www.AllStarGadget.com/product/${id}/${titleSlug}`;
    const shareText = `Check out ${product.name}`;
    if (navigator.share) {
      try { await navigator.share({ title: product.name, text: shareText, url: shareUrl }); }
      catch (error) { console.log('Error sharing:', error); }
    } else {
      navigator.clipboard.writeText(shareUrl);
      alert('Link copied to clipboard!');
    }
  };

  const scrollRelatedProducts = (direction) => {
    if (relatedContainerRef.current) {
      const scrollAmount = 300;
      const newPosition = direction === 'left'
        ? relatedScrollPosition - scrollAmount
        : relatedScrollPosition + scrollAmount;
      relatedContainerRef.current.scrollTo({ left: newPosition, behavior: 'smooth' });
      setRelatedScrollPosition(newPosition);
    }
  };

  useEffect(() => {
    const fetchPDPSettings = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/product-details-settings`);
        if (response.ok) {
          const data = await response.json();
          if (data.success && data.settings) setPdpSettings(data.settings);
        }
      } catch (error) { console.error('Error fetching PDP settings:', error); }
    };
    fetchPDPSettings();
  }, []);

  useEffect(() => {
    const fetchProductData = async () => {
      setLoading(true);
      setError(null);
      try {
        const url = `${AppConfig.API_BASE_URL}/products/${id}`;
        const response = await fetch(url, { headers: memoizedGetAuthHeaders() });
        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
        const data = await response.json();
        setProduct(data);
        setActiveImage(data.galleryImages?.[0] || data.image);
        setSelectedColor(data.colors?.[0]?.hex || '');
        setSelectedSize(data.sizes?.[0] || '');
      } catch (err) {
        setError("পণ্যটি লোড করা যায়নি। অনুগ্রহ করে পরে আবার চেষ্টা করুন।");
        setProduct(null);
      } finally {
        setLoading(false);
      }
    };
    if (id) { fetchProductData(); checkLoginStatus(); fetchUserProfile(); }
  }, [id, memoizedGetAuthHeaders, checkLoginStatus, fetchUserProfile]);

  useEffect(() => {
    const fetchRelatedProducts = async () => {
      if (!product) return;
      try {
        const response = await fetch(
          `${AppConfig.API_BASE_URL}/products?category=${product.category}&limit=10`,
          { headers: memoizedGetAuthHeaders() }
        );
        if (response.ok) {
          const data = await response.json();
          const filteredProducts = data.products.filter(p => p.productId !== product.productId);
          setRelatedProducts(filteredProducts.slice(0, 10));
        }
      } catch (error) { console.error('Error fetching related products:', error); }
    };
    fetchRelatedProducts();
  }, [product, memoizedGetAuthHeaders]);

  const hasFiredRef = useRef(false);

  useEffect(() => {
    if (hasFiredRef.current) return;
    function getFbClickId() { const match = document.cookie.match(/_fbc=([^;]*)/); return match ? match[1] : null; }
    function getFbBrowserId() { const match = document.cookie.match(/_fbp=([^;]*)/); return match ? match[1] : null; }
    const externalId = localStorage.getItem('client_id');
    const fbc = getFbClickId();
    const fbp = getFbBrowserId();
    const userData = { fbc, fbp, external_id: externalId };
    if (product && id) {
      const eventTime = Math.floor(Date.now() / 1000);
      ReactPixel.track('ViewContent', { content_name: product.name, content_category: product.category, content_ids: [product.productId], content_type: 'product', value: product.price, currency: 'BDT', event_id: eventId }, userData);
      ReactGA.event('view_item', { currency: 'BDT', value: product.price, items: [{ item_id: product.productId, item_name: product.name, item_category: product.category, price: product.price, quantity: 1 }], event_id: eventId });
      fetch(`${AppConfig.API_BASE_URL}/api/track-viewcontent`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ event_name: 'ViewContent', productId: product.productId, name: product.name, category: product.category, price: product.price, event_id: eventId, event_time: eventTime, fbc, fbp, external_id: externalId }) }).then(res => res.json()).then(data => console.log('ViewContent sent to Flask:', data)).catch(err => console.error('Error sending to Flask:', err));
      hasFiredRef.current = true;
    }
  }, [product, eventId, id]);

  useEffect(() => {
    let currentUserId = localStorage.getItem('user_session_id');
    if (!currentUserId) { currentUserId = uuidv4(); localStorage.setItem('user_session_id', currentUserId); }
    setUserSessionId(currentUserId);
    let interval;
    if (product && product.timer && product.time != null) {
      setTimerActive(true);
      const productTimerKey = `product_timer_${product.productId}_${currentUserId}`;
      const lastExpiredKey = `product_last_expired_${product.productId}_${currentUserId}`;
      let storedEndTime = localStorage.getItem(productTimerKey);
      let newEndTime;
      const now = new Date().getTime();
      if (storedEndTime) {
        newEndTime = parseInt(storedEndTime, 10);
        const lastExpiredTime = localStorage.getItem(lastExpiredKey);
        if (now >= newEndTime) {
          if (lastExpiredTime) {
            const timeSinceLastExpired = now - parseInt(lastExpiredTime, 10);
            if (timeSinceLastExpired >= TIMER_RESET_DAYS * 24 * 60 * 60 * 1000) {
              newEndTime = now + product.time * 60 * 1000;
              localStorage.setItem(productTimerKey, newEndTime.toString());
              localStorage.removeItem(lastExpiredKey);
            } else { setTimerActive(false); setRemainingTime(0); return; }
          } else { localStorage.setItem(lastExpiredKey, now.toString()); setTimerActive(false); setRemainingTime(0); return; }
        }
      } else {
        newEndTime = now + product.time * 60 * 1000;
        localStorage.setItem(productTimerKey, newEndTime.toString());
        localStorage.removeItem(lastExpiredKey);
      }
      interval = setInterval(() => {
        const currentTime = new Date().getTime();
        const timeLeft = newEndTime - currentTime;
        if (timeLeft <= 0) {
          clearInterval(interval);
          setRemainingTime(0);
          setTimerActive(false);
          if (!localStorage.getItem(lastExpiredKey)) localStorage.setItem(lastExpiredKey, currentTime.toString());
        } else { setRemainingTime(timeLeft); }
      }, 1000);
    } else { setTimerActive(false); setRemainingTime(0); }
    return () => { if (interval) clearInterval(interval); };
  }, [product, TIMER_RESET_DAYS]);

  useEffect(() => { window.scrollTo(0, 0); }, []);

  useEffect(() => {
    if (flyingCart) {
      const timer = setTimeout(() => setFlyingCart(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [flyingCart]);

  if (loading) {
    return (
      <PageWrapper>
        <ProductSection><p style={{ textAlign: 'center', padding: '40px', fontFamily: 'Tiro Bangla, serif' }}>পণ্য লোড হচ্ছে...</p></ProductSection>
      </PageWrapper>
    );
  }

  if (error) {
    return (
      <PageWrapper>
        <ProductSection>
          <BackLink to="/"><FaArrowLeft /> পণ্য তালিকায় ফিরে যান</BackLink>
          <p style={{ color: '#dc3545', marginTop: 16 }}>{error}</p>
        </ProductSection>
      </PageWrapper>
    );
  }

  if (!product) {
    return (
      <PageWrapper>
        <ProductSection>
          <BackLink to="/"><FaArrowLeft /> পণ্য তালিকায় ফিরে যান</BackLink>
          <p style={{ marginTop: 16 }}>পণ্যটি খুঁজে পাওয়া যায়নি!</p>
        </ProductSection>
      </PageWrapper>
    );
  }

  const handleAddToCart = () => {
    addToCart({
      ...product,
      selectedColor: product.colors?.find(c => c.hex === selectedColor) || null,
      selectedSize: selectedSize || null,
    });
    function getFbClickId() { const match = document.cookie.match(/_fbc=([^;]*)/); return match ? match[1] : null; }
    function getFbBrowserId() { const match = document.cookie.match(/_fbp=([^;]*)/); return match ? match[1] : null; }
    const externalId = localStorage.getItem('client_id');
    const fbc = getFbClickId();
    const fbp = getFbBrowserId();
    const userData = { fbc, fbp, external_id: externalId };
    const eventTime = Math.floor(Date.now() / 1000);
    ReactPixel.track('AddToCart', { content_name: product.name, content_category: product.category, content_ids: [product.productId], content_type: 'product', value: product.price, event_id: eventId, currency: 'BDT' }, userData);
    ReactGA.event('add_to_cart', { currency: 'BDT', value: product.price, items: [{ item_id: product.productId, item_name: product.name, item_category: product.category, price: product.price, quantity: 1 }], event_id: eventId });
    if (addCartButtonRef.current) {
      const rect = addCartButtonRef.current.getBoundingClientRect();
      setFlyingCart({ id: Date.now(), startX: rect.left + rect.width / 2, startY: rect.top + rect.height / 2 });
    }
    fetch(`${AppConfig.API_BASE_URL}/api/track-addtocart`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ event_name: 'AddToCart', productId: product.productId, name: product.name, price: product.price, color: selectedColor, size: selectedSize, event_id: eventId, event_time: eventTime, fbc, fbp, external_id: externalId }) }).then(res => res.json()).then(data => console.log('AddToCart sent to Flask:', data)).catch(err => console.error('Error sending AddToCart to Flask:', err));
  };

  const hasStock = product.stock > 0;
  const productReviews = product.reviews || [];
  const averageRating = productReviews.length > 0
    ? (productReviews.reduce((acc, r) => acc + r.rating, 0) / productReviews.length)
    : 0;

  const renderStars = (rating) => {
    const stars = [];
    for (let i = 1; i <= 5; i++) {
      if (rating >= i) stars.push(<FaStar key={i} />);
      else if (rating >= i - 0.5) stars.push(<FaStarHalfAlt key={i} />);
      else stars.push(<FaRegStar key={i} style={{ color: '#ccc' }} />);
    }
    return stars;
  };

  const handleWhatsAppClick = () => {
    const titleSlug = slugify(product.name, { lower: true, strict: true, trim: true });
    const link = `https://www.AllStarGadget.com/product/${id}/${titleSlug}`;
    const message = `আমি জানতে চাই আপনাদের ${product.name} প্রোডাক্ট সম্পর্কে ${link}`;
    const encodedMessage = encodeURIComponent(message);
    const whatsappNumber = pdpSettings.whatsappButton.phoneNumber;
    window.open(`https://wa.me/${whatsappNumber}?text=${encodedMessage}`, '_blank');
  };

  const handleMessengerClick = () => {
    window.open(pdpSettings.messengerButton.messengerLink, '_blank');
  };

  const handleClick = () => {
    window.location.href = `tel:${pdpSettings.callButton.phoneNumber}`;
  };

  const handleProceedToCheckout = () => {
    const productToAddForCheckout = {
      ...product,
      quantity: 1,
      selectedColor: product.colors?.find(c => c.hex === selectedColor) || null,
      selectedSize: selectedSize || null,
    };
    let cartItemsForNavigation = [];
    const existingIndex = cartItem.findIndex(item => item.productId === productToAddForCheckout.productId);
    if (existingIndex !== -1) {
      cartItemsForNavigation = cartItem.map((item, index) =>
        index === existingIndex ? { ...item, quantity: item.quantity + 1 } : item
      );
    } else {
      cartItemsForNavigation = [...cartItem, productToAddForCheckout];
    }
    const totalForNavigation = cartItemsForNavigation.reduce((acc, item) => {
      const price = typeof item.price === 'number' ? item.price : parseFloat(item.price) || 0;
      const quantity = typeof item.quantity === 'number' ? item.quantity : parseInt(item.quantity) || 0;
      return acc + (price * quantity);
    }, 0);
    addToCart({
      ...product,
      selectedColor: product.colors?.find(c => c.hex === selectedColor) || null,
      selectedSize: selectedSize || null,
    });
    navigate('/checkout', { state: { cartItems: cartItemsForNavigation, total: totalForNavigation } });
  };

  const formatTime = (milliseconds) => {
    const totalSeconds = Math.floor(milliseconds / 1000);
    const days = Math.floor(totalSeconds / (3600 * 24));
    const hours = Math.floor((totalSeconds % (3600 * 24)) / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    return { days, hours, minutes, seconds };
  };

  const formattedTime = formatTime(remainingTime);

  const schemaData = {
    "@context": "https://schema.org/",
    "@type": "Product",
    "name": product.name,
    "image": product.image,
    "description": product.description,
    "sku": product.sku || product.id,
    "offers": {
      "@type": "Offer",
      "priceCurrency": "BDT",
      "price": product.price,
      "availability": product.stock ? "https://schema.org/InStock" : "https://schema.org/OutOfStock"
    }
  };

  const handleAddReviewClick = () => {
    if (!isLoggedIn) { navigate('/login'); return; }
    setShowReviewModal(true);
  };

  const handleReviewSubmit = async (rating, comment, submittedUserName) => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) { setShowReviewModal(false); navigate('/login'); return; }
      const response = await fetch(`${API_BASE_URL}/api/add-product-review`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ productId: product.productId, rating, comment, customerName: submittedUserName }),
      });
      if (!response.ok) { const errorData = await response.json(); throw new Error(errorData.error || 'রিভিউ যোগ করতে সমস্যা হয়েছে।'); }
      setShowReviewModal(false);
      const updatedProductResponse = await fetch(`${AppConfig.API_BASE_URL}/products/${id}`, { headers: memoizedGetAuthHeaders() });
      if (updatedProductResponse.ok) { const updatedProductData = await updatedProductResponse.json(); setProduct(updatedProductData); }
    } catch (err) { console.error("রিভিউ সাবমিট করতে সমস্যা:", err); }
  };

  return (
    <>
      <GlobalStyle />
      <Helmet>
        <title>{product.name} | {AppConfig.CompanyName}</title>
        <meta name="description" content={product.description || `${AppConfig.CompanyName} বাংলাদেশের একমাত্র সহজলভ্য ও দ্রুত ডেলিভারি দেওয়া ই-কমার্স প্রতিষ্ঠান`} />
        <meta name="keywords" content={`ElevateMart, সহজলভ্য, দ্রুত ডেলিভারি, ই-কমার্স প্রতিষ্ঠান, ${product.name}, ${product.category}`} />
        <meta property="og:image" content={product.image} />
        <meta property="og:type" content="product" />
        <meta property="og:title" content={product.name} />
        <meta property="og:description" content={product.description} />
        <meta property="og:url" content={`https://www.AllStarGadget.com/product/${id}/${slugify(product.name, { lower: true, strict: true, trim: true })}`} />
        <script type="application/ld+json">{JSON.stringify(schemaData)}</script>
      </Helmet>

      <PageWrapper>
        {/* Breadcrumb */}
        <Breadcrumb>
          <BackLink to="/"><FaArrowLeft /> পণ্য তালিকায় ফিরে যান</BackLink>
        </Breadcrumb>

        {/* Product Section */}
        <ProductSection>
          <ProductGrid>
            {/* Left — Images */}
            <ImageGallery>
              <StickyImageInner>
                <div style={{ position: 'relative' }}>
                  {product.discount && !product.isPopular && <BadgeDiscount>{product.discount}</BadgeDiscount>}
                  {product.isPopular && <BadgePopular>Best Seller</BadgePopular>}
                  <FavoriteIcon onClick={() => toggleFavorite(product.productId)} isFav={isFavorite(product.productId)}>
                    {isFavorite(product.productId) ? <FilledHeart style={{ color: '#ff0000' }} /> : <EmptyHeart style={{ color: '#ccc' }} />}
                  </FavoriteIcon>
                  <ImageZoom src={activeImage} alt={product.name} />
                </div>

                <ThumbnailsContainer>
                  {product.galleryImages && product.galleryImages.length > 0 ? (
                    product.galleryImages.map((img, index) => (
                      <Thumbnail key={index} active={activeImage === img} onClick={() => setActiveImage(img)}>
                        <img src={img} alt={`${product.name} - ${index + 1}`} />
                      </Thumbnail>
                    ))
                  ) : (
                    <Thumbnail active onClick={() => setActiveImage(product.image)}>
                      <img src={product.image} alt={product.name} />
                    </Thumbnail>
                  )}
                </ThumbnailsContainer>
              </StickyImageInner>
            </ImageGallery>

            {/* Middle — Info */}
            <InfoWrapper>
              <ProductName>{product.name}</ProductName>

              {/* Timer */}
              {timerActive && remainingTime > 0 && (
                <TimerContainer>
                  {formattedTime.days > 0 && (
                    <TimeBox>
                      <TimeValue>{String(formattedTime.days).padStart(2, '0')}</TimeValue>
                      <TimeLabel>দিন</TimeLabel>
                    </TimeBox>
                  )}
                  {(formattedTime.hours > 0 || formattedTime.days > 0) && (
                    <TimeBox>
                      <TimeValue>{String(formattedTime.hours).padStart(2, '0')}</TimeValue>
                      <TimeLabel>ঘন্টা</TimeLabel>
                    </TimeBox>
                  )}
                  <TimeBox>
                    <TimeValue>{String(formattedTime.minutes).padStart(2, '0')}</TimeValue>
                    <TimeLabel>মিনিট</TimeLabel>
                  </TimeBox>
                  <TimeBox>
                    <TimeValue>{String(formattedTime.seconds).padStart(2, '0')}</TimeValue>
                    <TimeLabel>সেকেন্ড</TimeLabel>
                  </TimeBox>
                </TimerContainer>
              )}

              {/* Rating */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <RatingDisplay>
                  {renderStars(averageRating)}
                  {productReviews.length > 0 ? (
                    <span>({averageRating.toFixed(1)} / {productReviews.length} রিভিউ)</span>
                  ) : (
                    <span>(কোনো রিভিউ নেই)</span>
                  )}
                </RatingDisplay>
              </div>

              {/* Stock */}
              <StockInfo hasStock={hasStock}>
                {hasStock ? `স্টকে আছে: ${product.stock} টি` : 'স্টক শেষ'}
              </StockInfo>

              {/* Buyers */}
              {product.buyers > 0 && <BuyersInfo>{product.buyers} জন কিনেছেন</BuyersInfo>}

              {/* Price */}
              <PriceContainer>
                <CurrentPrice>৳{product.price}</CurrentPrice>
                {product.originalPrice && product.originalPrice > product.price && (
                  <>
                    <OriginalPrice>৳{product.originalPrice}</OriginalPrice>
                    <DiscountBadge>-{Math.round(((product.originalPrice - product.price) / product.originalPrice) * 100)}%</DiscountBadge>
                  </>
                )}
              </PriceContainer>

              {/* Color */}
              {product.isColorAvailable && product.colors && product.colors.length > 0 && (
                <OptionGroup>
                  <h4>রং নির্বাচন করুন: {product.colors.find(c => c.hex === selectedColor)?.name || ''}</h4>
                  {product.colors.map((color, index) => (
                    <ColorOptionWrapper key={index} active={selectedColor === color.hex} onClick={() => setSelectedColor(color.hex)}>
                      <ColorBox colorHex={color.hex} />
                      <span>{color.name}</span>
                    </ColorOptionWrapper>
                  ))}
                </OptionGroup>
              )}

              {/* Size */}
              {product.isSizeAvailable && product.sizes && product.sizes.length > 0 && (
                <OptionGroup>
                  <h4>আকার নির্বাচন করুন: <span style={{ color: '#e67e22' }}>{selectedSize}</span></h4>
                  <div>
                    {product.sizes.map((size, index) => (
                      <SizeBox key={index} active={selectedSize === size} onClick={() => setSelectedSize(size)}>
                        {size}
                      </SizeBox>
                    ))}
                  </div>

                  {/* Size Chart */}
                  {product.sizeChart && product.sizeChart.length > 0 && product.sizeChartColumns && product.sizeChartColumns.length > 0 && (
                    <div style={{ marginTop: 8 }}>
                      <SizeChartToggle onClick={() => setShowSizeChart(prev => !prev)}>
                        📏 {showSizeChart ? 'Hide' : 'View'} Size Chart
                      </SizeChartToggle>
                      {showSizeChart && (
                        <div style={{ border: '1px solid #e9ecef', borderRadius: 8, overflow: 'hidden' }}>
                          <div style={{ background: '#f0ad4e', padding: '4px 10px' }}><span style={{ color: '#fff', fontWeight: 'bold', fontSize: '0.8em' }}>INCH</span></div>
                          <div style={{ overflowX: 'auto' }}>
                            <SizeChartTable>
                              <thead><tr><th>Size</th>{product.sizeChartColumns.map(col => <th key={col}>{col}</th>)}</tr></thead>
                              <tbody>
                                {product.sizeChart.map((row, idx) => (
                                  <tr key={idx} style={{ background: row.size === selectedSize ? '#fff3e0' : '' }}>
                                    <td style={{ fontWeight: 'bold' }}>{row.size === selectedSize ? `▶ ${row.size}` : row.size}</td>
                                    {product.sizeChartColumns.map(col => <td key={col}>{row[`${col}_inch`] || '—'}</td>)}
                                  </tr>
                                ))}
                              </tbody>
                            </SizeChartTable>
                          </div>
                          <div style={{ background: '#5cb85c', padding: '4px 10px', marginTop: 8 }}><span style={{ color: '#fff', fontWeight: 'bold', fontSize: '0.8em' }}>CM</span></div>
                          <div style={{ overflowX: 'auto' }}>
                            <SizeChartTable>
                              <thead><tr><th>Size</th>{product.sizeChartColumns.map(col => <th key={col}>{col}</th>)}</tr></thead>
                              <tbody>
                                {product.sizeChart.map((row, idx) => (
                                  <tr key={idx} style={{ background: row.size === selectedSize ? '#fff3e0' : '' }}>
                                    <td style={{ fontWeight: 'bold' }}>{row.size === selectedSize ? `▶ ${row.size}` : row.size}</td>
                                    {product.sizeChartColumns.map(col => <td key={col}>{row[`${col}_cm`] || '—'}</td>)}
                                  </tr>
                                ))}
                              </tbody>
                            </SizeChartTable>
                          </div>
                          <p style={{ fontSize: '0.7em', color: '#aaa', padding: '6px 10px', background: '#f8f9fa', margin: 0 }}>* Expected deviation &lt; 3%</p>
                        </div>
                      )}
                    </div>
                  )}
                </OptionGroup>
              )}

              {/* Share */}
              <ShareButton onClick={handleShare}>
                <FaShare /> Share this product
              </ShareButton>
            </InfoWrapper>

            {/* Right — Buy Box */}
            <div>
              <BuyBox>
                <BuyBoxPrice>৳{product.price}</BuyBoxPrice>
                {product.originalPrice && product.originalPrice > product.price && (
                  <BuyBoxOriginalPrice>List: <span style={{ textDecoration: 'line-through' }}>৳{product.originalPrice}</span></BuyBoxOriginalPrice>
                )}
                <BuyBoxStock hasStock={hasStock}>
                  {hasStock ? '✓ স্টকে আছে' : '✗ স্টক শেষ'}
                  {hasStock && <div style={{ fontWeight: 'normal', color: '#666', marginTop: 4, fontSize: '0.9em' }}>Ships from Bangladesh</div>}
                </BuyBoxStock>

                <BuyBoxButtons>
                  <ActionButtonPrimary onClick={handleProceedToCheckout} disabled={!hasStock}>
                    {hasStock ? 'Order Now' : 'স্টক নেই'}
                  </ActionButtonPrimary>

                  <ActionButtonSecondary ref={addCartButtonRef} onClick={handleAddToCart} disabled={!hasStock}>
                    <FaShoppingCart style={{ marginRight: 6 }} />
                    {hasStock ? 'Add to Cart' : 'স্টক নেই'}
                  </ActionButtonSecondary>

                  <TwoColumnButtons>
                    {pdpSettings.whatsappButton.enabled && (
                      <ActionButtonWhatsApp onClick={handleWhatsAppClick}>
                        <FontAwesomeIcon icon={faWhatsapp} style={{ marginRight: 4 }} />
                        {pdpSettings.whatsappButton.text}
                      </ActionButtonWhatsApp>
                    )}

                    {pdpSettings.messengerButton.enabled && (
                      <ActionButtonMessenger
                        onClick={handleMessengerClick}
                        spanFull={pdpSettings.whatsappButton.enabled ? false : true}
                      >
                        <FontAwesomeIcon icon={faFacebookMessenger} style={{ marginRight: 4 }} />
                        {pdpSettings.messengerButton.text}
                      </ActionButtonMessenger>
                    )}
                  </TwoColumnButtons>
                </BuyBoxButtons>

                {(pdpSettings.secureTransaction.enabled || pdpSettings.cashOnDelivery.enabled) && (
                  <TrustBadges>
                    {pdpSettings.secureTransaction.enabled && <div>{pdpSettings.secureTransaction.text}</div>}
                    {pdpSettings.cashOnDelivery.enabled && <div>{pdpSettings.cashOnDelivery.text}</div>}
                  </TrustBadges>
                )}
              </BuyBox>
            </div>
          </ProductGrid>

          {/* Product Details Tabs */}
          <TabsSection id="product-tabs">
            <TabButtons>
              <TabButton active={activeTab === 'specification'} onClick={() => setActiveTab('specification')}>
                Specifications
              </TabButton>
              <TabButton active={activeTab === 'reviews'} onClick={() => setActiveTab('reviews')}>
                Reviews ({productReviews.length})
              </TabButton>
            </TabButtons>

            {activeTab === 'specification' && (
              <div>
                {product.specifications && product.specifications.length > 0 ? (
                  <SpecContainer>
                    <h3 style={{ fontSize: '1em', fontWeight: 'bold', color: '#333', marginBottom: 12 }}>Details</h3>
                    <div className="stylish-spec-list">
                      {product.specifications.map((spec, index) => (
                        <div key={index} className="spec-item">
                          <div className="spec-key">
                            <span className="spec-bullet" />
                            <strong>{spec.key}</strong>
                          </div>
                          <div className="spec-value">{spec.value}</div>
                        </div>
                      ))}
                    </div>
                  </SpecContainer>
                ) : (
                  <p style={{ color: '#888', fontFamily: 'Tiro Bangla, serif' }}>এই পণ্যের জন্য কোনো স্পেসিফিকেশন নেই।</p>
                )}

                {/* Full Description */}
                {product.description && (
                  <DescriptionSection>
                    <h3>পণ্যের বিবরণ</h3>
                    <div>
                      {product.description.split('\n').map((line, index) => {
                        if (!line.trim()) return null;
                        const isSpecialLine = line.trim().startsWith('**');
                        const cleanLine = isSpecialLine ? line.trim().substring(2) : line.trim();
                        return (
                          <DescriptionItem key={index} isSpecial={isSpecialLine}>
                            <span className="icon">{isSpecialLine ? '✨' : '•'}</span>
                            <span className="text"><ReactMarkdown>{cleanLine}</ReactMarkdown></span>
                          </DescriptionItem>
                        );
                      })}
                    </div>
                  </DescriptionSection>
                )}

                {/* YouTube Video */}
                {product.youtubeUrl && (
                  <VideoSection>
                    <h3>পণ্যের ভিডিও</h3>
                    <YouTubeEmbed>
                      <iframe
                        src={product.youtubeUrl}
                        title="Product Video"
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      />
                    </YouTubeEmbed>
                  </VideoSection>
                )}
              </div>
            )}

            {activeTab === 'reviews' && (
              <div>
                <h3 style={{ fontSize: '1em', fontWeight: 'bold', color: '#333', marginBottom: 12, fontFamily: 'Tiro Bangla, serif' }}>রিভিউ</h3>

                {productReviews.length > 0 ? (
                  <>
                    <RatingSummary>
                      <div>
                        <div className="avg">{averageRating.toFixed(1)}</div>
                        <div className="stars">{renderStars(averageRating)}</div>
                        <div className="count">{productReviews.length} রিভিউ</div>
                      </div>
                    </RatingSummary>
                    {productReviews.map((review) => (
                      <ReviewCard key={review.reviewId}>
                        <ReviewHeader>
                          <span className="name">{review.userName}</span>
                          <span className="stars">{renderStars(review.rating)}</span>
                        </ReviewHeader>
                        <ReviewComment>{review.comment}</ReviewComment>
                      </ReviewCard>
                    ))}
                  </>
                ) : (
                  <p style={{ color: '#888', fontFamily: 'Tiro Bangla, serif' }}>এই পণ্যের জন্য কোনো রিভিউ নেই।</p>
                )}

                <AddReviewSection>
                  <button onClick={handleAddReviewClick}>
                    <FontAwesomeIcon icon={faComments} style={{ marginRight: 6 }} />
                    একটি রিভিউ যোগ করুন
                  </button>
                </AddReviewSection>
              </div>
            )}
          </TabsSection>
        </ProductSection>

        {/* Related Products */}
        {relatedProducts.length > 0 && (
          <RelatedSection>
            <RelatedInner>
              <h2>Related items</h2>

              {/* Mobile: 2-col grid */}
              <RelatedGrid>
                {relatedProducts.map((relatedProduct) => (
                  <div key={relatedProduct.productId}>
                    <ProductCard
                      product={relatedProduct}
                      addToCart={addToCart}
                      toggleFavorite={toggleFavorite}
                      isFavorite={isFavorite}
                      cartItems={cartItem}
                      navigate={navigate}
                    />
                  </div>
                ))}
              </RelatedGrid>

              {/* Desktop: horizontal scroll */}
              <RelatedScroll>
                <ScrollArrow dir="left" onClick={() => scrollRelatedProducts('left')}>
                  <FaChevronLeft />
                </ScrollArrow>
                <RelatedScrollInner ref={relatedContainerRef}>
                  <RelatedFlex>
                    {relatedProducts.map((relatedProduct) => (
                      <div key={relatedProduct.productId}>
                        <ProductCard
                          product={relatedProduct}
                          addToCart={addToCart}
                          toggleFavorite={toggleFavorite}
                          isFavorite={isFavorite}
                          cartItems={cartItem}
                          navigate={navigate}
                        />
                      </div>
                    ))}
                  </RelatedFlex>
                </RelatedScrollInner>
                <ScrollArrow dir="right" onClick={() => scrollRelatedProducts('right')}>
                  <FaChevronRight />
                </ScrollArrow>
              </RelatedScroll>
            </RelatedInner>
          </RelatedSection>
        )}
      </PageWrapper>

      {/* Flying Cart */}
      {flyingCart && (
        <FlyingCartItem style={{ left: flyingCart.startX, top: flyingCart.startY }}>
          <FaShoppingCart /> +1
        </FlyingCartItem>
      )}

      {/* Review Modal */}
      {showReviewModal && (
        <ReviewModal
          onClose={() => setShowReviewModal(false)}
          onSubmit={handleReviewSubmit}
          productName={product.name}
          userName={currentUserName}
          isLoggedIn={isLoggedIn}
        />
      )}
    </>
  );
}

export default ProductDetails;